"use client";
import Image from "next/image";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import {
  formatCurrency,
  getErrorMessage,
  getImageUrl,
  useModalState,
} from "@/lib";
import { updateLineItems, deleteLineItems } from "../services";
import { CartSchema, LineItemsSchema } from "../types";
import { Button, Input } from "@/components";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/24/solid";
import DeleteModal from "@/components/molecules/modal/delete";
import { useStoreContext } from "@/lib/store";

interface CartItemsInterface {
  dataIsLoading: boolean;
  callbackAfterItemSave: () => void;
  cart: CartSchema;
}

export default function CartItems(props: CartItemsInterface) {
  const { cart, callbackAfterItemSave, dataIsLoading } = props;
  const { openModal, closeModal, modalIsOpen } = useModalState();
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<LineItemsSchema>();
  const [cartItems, setCartItems] = useState<
    { productName: string; variants: LineItemsSchema[] }[]
  >([]);
  const [originalCartItems, setOriginalCartItems] = useState<LineItemsSchema[]>(
    [],
  );

  useEffect(() => {
    const groupedItems = cart?.lineItems.reduce(
      (acc, item) => {
        const existingGroup = acc.find(
          (group) => group.productName === item.productName,
        );
        if (existingGroup) {
          existingGroup.variants.push(item);
        } else {
          acc.push({ productName: item.productName, variants: [item] });
        }
        return acc;
      },
      [] as { productName: string; variants: LineItemsSchema[] }[],
    );
    setCartItems(groupedItems ?? []);
    setOriginalCartItems(cart?.lineItems ?? []);
  }, [cart]);

  const handleQuantityChange = (
    variantId: string,
    newQuantity: number,
    minimumOrderQuantity: number,
  ) => {
    if (newQuantity < minimumOrderQuantity) {
      toast.warn(`Minimum Order Quantity is ${minimumOrderQuantity}`);
      return;
    }

    setCartItems((prevItems) => {
      const updatedItems = prevItems.flatMap((item) =>
        item.variants.map((variant) =>
          variant.variantId === variantId
            ? { ...variant, quantity: newQuantity }
            : variant,
        ),
      );
      const regroupedItems = updatedItems.reduce(
        (acc, item) => {
          const existingGroup = acc.find(
            (group) => group.productName === item.productName,
          );
          if (existingGroup) {
            existingGroup.variants.push(item);
          } else {
            acc.push({ productName: item.productName, variants: [item] });
          }
          return acc;
        },
        [] as { productName: string; variants: LineItemsSchema[] }[],
      );
      return regroupedItems;
    });
  };

  const handleCancelEdit = (variantId: string) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.variants.some((variant) => variant.variantId === variantId)
          ? {
              ...item,
              variants: item.variants.map((variant) =>
                variant.variantId === variantId
                  ? (originalCartItems.find(
                      (original) => original.variantId === variantId,
                    ) ?? variant)
                  : variant,
              ),
            }
          : item,
      ),
    );
    setEditingItem(null);
  };

  const handleSaveItem = async (item: LineItemsSchema) => {
    setEditLoading(true);
    const response = await updateLineItems(cart?._id ?? "", [item]);
    if (response.error) {
      setEditLoading(false);
      callbackAfterItemSave();
      toast.error(getErrorMessage(response));
      return;
    }
    toast.success(`Quantity updated!`);
    setEditingItem(null);
    setEditLoading(false);
    callbackAfterItemSave();
  };

  if (dataIsLoading) {
    return <p>Cart is loading...</p>;
  }

  return (
    <>
      <div className="flex-grow overflow-y-scroll">
        <div className="flex flex-col space-y-3">
          {cartItems?.map((cartItem, index) => {
            return (
              <div
                key={`${cartItem.productName}` + index}
                className="bg-white border border-zinc-950/10 rounded-md pt-3 pb-2 px-4">
                <p className="text-sm md:text-base font-semibold pb-2">
                  {cartItem.productName}
                </p>
                <ul
                  role="list"
                  className="divide-y divide-gray-200 text-sm font-medium text-gray-900">
                  {cartItem.variants.map((item: LineItemsSchema) => {
                    const isEditing = editingItem === item.variantId;
                    return (
                      <li
                        key={item.variantId}
                        className="flex flex-col py-3.5 w-full">
                        <div className="flex items-start md:items-center space-x-4 md:space-x-6 justify-between">
                          <Image
                            src={getImageUrl(item.images[0])}
                            alt={`${item.productName} image`}
                            height={500}
                            width={500}
                            className="size-24 flex-none rounded-md object-cover"
                            unoptimized={true}
                          />
                          <div className="hidden md:flex md:flex-1 flex-col space-y-1">
                            {item.variantTitle.split(";").map((each) => (
                              <p key={each} className="text-sm text-dark">
                                <span className="font-semibold">
                                  {each.split(":")[0]}:
                                </span>
                                {each.split(":").slice(1).join(":")}
                              </p>
                            ))}
                          </div>
                          <div className="flex flex-col justify-between items-end space-y-3">
                            <p className="text-base font-semibold">
                              {formatCurrency(item.price * item.quantity * 100)}
                            </p>
                            <div className="flex justify-end items-center space-x-2">
                              <Input
                                type="number"
                                value={item.quantity}
                                min={item.minimumOrderQuantity}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    item.variantId,
                                    Number(e.target.value),
                                    item.minimumOrderQuantity,
                                  )
                                }
                                className="!w-20"
                                disabled={!isEditing}
                              />
                              <Button
                                outline
                                className="!h-[43px] !px-[17px]"
                                onClick={() => setEditingItem(item.variantId)}>
                                <PencilSquareIcon className="size-6 text-gray-500" />
                              </Button>
                              <Button
                                outline
                                className="!h-[43px] !px-[17px]"
                                onClick={() => {
                                  openModal();
                                  setSelectedItem(item);
                                }}>
                                <TrashIcon className="size-6 !text-red-400" />
                              </Button>
                            </div>
                            {isEditing && (
                              <div className="flex gap-2">
                                <Button
                                  outline
                                  onClick={() =>
                                    handleCancelEdit(item.variantId)
                                  }>
                                  Cancel
                                </Button>
                                <Button
                                  color="primary"
                                  onClick={() => handleSaveItem(item)}
                                  loading={editLoading}>
                                  Save
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="w-full md:hidden flex flex-col space-y-1 pt-3">
                          {item.variantTitle.split(";").map((each) => (
                            <p key={each} className="text-sm text-dark">
                              <span className="font-semibold">
                                {each.split(":")[0]}:
                              </span>
                              {each.split(":").slice(1).join(":")}
                            </p>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
      <DeleteModal
        handleDelete={async () => {
          setLoading(true);
          try {
            if (selectedItem)
              await deleteLineItems(cart?._id ?? "", [selectedItem._id]);
            toast.success("Removed!");
            callbackAfterItemSave();
            closeModal();
          } catch (err) {
            toast.error(`Unable to remove item - ${err}`);
          } finally {
            setLoading(false);
          }
        }}
        loading={loading}
        isOpen={modalIsOpen}
        handleOpen={openModal}
        handleClose={closeModal}
        title="Remove from cart"
        description="Are you sure you want to delete this item?"
      />
    </>
  );
}
